export function useImageDownscale() {
    /**
     * Re-encode a picked photo as a JPEG no larger than maxEdge on its long side.
     *
     * Runs on every file, however small: drawing through a canvas is also what strips EXIF, and
     * phone EXIF carries GPS coordinates — publishing a customer's home location beside a
     * health-related review is a privacy incident, so a "small enough to skip" branch would
     * reintroduce the leak. `canvas.toBlob` and `URL.createObjectURL` clear this project's Safari
     * 13 floor; `createImageBitmap` and `OffscreenCanvas` do not, and the build target lowers
     * syntax without polyfilling runtime APIs.
     */
    function downscale(file: Blob, maxEdge = 1600): Promise<Blob> {
        return new Promise((resolve, reject) => {
            const objectUrl = URL.createObjectURL(file);
            const image = new Image();

            image.onload = () => {
                URL.revokeObjectURL(objectUrl);

                const scale = Math.min(
                    1,
                    maxEdge / Math.max(image.width, image.height),
                );
                const canvas = document.createElement("canvas");
                canvas.width = Math.round(image.width * scale);
                canvas.height = Math.round(image.height * scale);

                const context = canvas.getContext("2d");
                if (!context) {
                    reject(
                        new Error("This browser could not process the photo."),
                    );
                    return;
                }

                context.drawImage(image, 0, 0, canvas.width, canvas.height);
                canvas.toBlob(
                    (blob) =>
                        blob
                            ? resolve(blob)
                            : reject(
                                  new Error(
                                      "This browser could not process the photo.",
                                  ),
                              ),
                    "image/jpeg",
                    0.85,
                );
            };

            image.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                reject(new Error("That file could not be read as a photo."));
            };

            image.src = objectUrl;
        });
    }

    return { downscale };
}
